import { REDACTED_TERMS } from '../constants/redactedTerms';

export const REDACTED_PLACEHOLDER = '[Redacted]';

/**
 * The subset of a Sentry `Event` this scrubber reads or mutates. Typed
 * structurally rather than against any `@sentry/*` package so the same scrubber
 * works across every SDK and major version in the monorepo (`@sentry/node`,
 * `@sentry/react`, `@sentry/react-router`, `@sentry/react-native`) without a
 * dependency on, or version coupling to, any of them.
 */
export interface ScrubbableSentryEvent {
  message?: string;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  request?: {
    url?: string;
    query_string?: unknown;
    headers?: Record<string, unknown>;
    cookies?: unknown;
    data?: unknown;
  };
  breadcrumbs?: Array<{
    message?: string;
    data?: Record<string, unknown>;
  }>;
  exception?: {
    values?: Array<{
      value?: string;
      stacktrace?: {
        frames?: Array<{ vars?: Record<string, unknown> }>;
      };
    }>;
  };
}

// Sentry-side redaction matches by substring: a key is redacted when its
// lower-cased form *contains* any sensitive term, so derived names like
// `confirmPassword`, `accessToken` and `clientSecret` would be caught here by
// the base terms (`password`, `token`, `secret`) even if they were not listed.
// They are listed anyway, because the pino logger matches keys exactly and
// would otherwise log them in plaintext. This side is deliberately broader and
// over-redacts benign keys (e.g. `cookieConsent`, `tokenCount`); for error
// monitoring, over-redaction is the safe failure mode.
const SENSITIVE_STEMS = REDACTED_TERMS.map((term) => term.toLowerCase());

const isSensitiveKey = (key: string): boolean => {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_STEMS.some((stem) => lowerKey.includes(stem));
};

// Matches a JSON `"key": value` pair where the value is either a quoted string
// or a bare literal (number / boolean / null), capturing the key, separator and
// value. Used to redact secrets serialised into a string — e.g. an error
// message that embeds a JSON request body, as `graphql-request` does in its
// `ClientError` message. The literal alternative catches numeric/boolean
// secrets (e.g. a numeric OTP) that wouldn't be wrapped in quotes.
const JSON_KEY_VALUE =
  /"([^"]+)"(\s*:\s*)("(?:\\.|[^"\\])*"|-?\d+(?:\.\d+)?|true|false|null)/g;

// A `?name=value` / `&name=value` query parameter inside a URL-looking string,
// for secrets that live in a URL embedded in a free-text message rather than in
// the structured `request.url` field.
const URL_QUERY_PARAM = /([?&])([^=&\s#]+)=([^&\s#]*)/g;

// `Bearer <token>` / `Basic <token>` credentials interpolated into a message.
const AUTH_SCHEME_TOKEN = /\b(Bearer|Basic)\s+[A-Za-z0-9\-._~+/=]+/gi;

// Credentials embedded in URL userinfo (`scheme://user:secret@host`); redacts
// the password portion, keeping the username.
const URL_USERINFO = /(:\/\/[^/?#\s@:]*:)[^/?#\s@]+(@)/g;

// Redact secrets that have been serialised into a string in any of the forms
// above. Each pass is independent, so a single string can carry more than one.
const redactSecretsInString = (value: string): string =>
  value
    .replace(JSON_KEY_VALUE, (match, key, separator) =>
      isSensitiveKey(key)
        ? `"${key}"${separator}"${REDACTED_PLACEHOLDER}"`
        : match
    )
    .replace(URL_QUERY_PARAM, (match, prefix, name) =>
      isSensitiveKey(name) ? `${prefix}${name}=${REDACTED_PLACEHOLDER}` : match
    )
    .replace(
      AUTH_SCHEME_TOKEN,
      (_match, scheme) => `${scheme} ${REDACTED_PLACEHOLDER}`
    )
    .replace(
      URL_USERINFO,
      (_match, prefix, suffix) => `${prefix}${REDACTED_PLACEHOLDER}${suffix}`
    );

// Redact the value of any query parameter whose name looks sensitive, keeping
// the rest of the query string intact. Operates on a raw `name=value&…` string
// (not a parsed object) so it can reach secrets that live inside the request
// URL / query string, which key-based object recursion cannot see.
const redactQueryString = (queryString: string): string =>
  queryString
    .split('&')
    .map((pair) => {
      const separatorIndex = pair.indexOf('=');
      if (separatorIndex === -1) return pair;
      const name = pair.slice(0, separatorIndex);
      return isSensitiveKey(name) ? `${name}=${REDACTED_PLACEHOLDER}` : pair;
    })
    .join('&');

// Redact sensitive query parameters embedded in a full request URL.
const redactUrl = (url: string): string => {
  const queryIndex = url.indexOf('?');
  if (queryIndex === -1) return url;
  const base = url.slice(0, queryIndex);
  const query = url.slice(queryIndex + 1);
  return `${base}?${redactQueryString(query)}`;
};

// Bound recursion so a pathologically deep payload can't stall event delivery.
const MAX_DEPTH = 8;

// Walk an arbitrary value, redacting any property whose key looks sensitive and
// scrubbing secrets embedded in string leaves. Mutates in place; the `seen` set
// guards against cycles.
const redactInPlace = (
  value: unknown,
  seen: WeakSet<object>,
  depth: number
): void => {
  if (depth > MAX_DEPTH || value === null || typeof value !== 'object') {
    return;
  }
  if (seen.has(value)) return;
  seen.add(value);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      if (typeof item === 'string') {
        value[index] = redactSecretsInString(item);
      } else {
        redactInPlace(item, seen, depth + 1);
      }
    });
    return;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const child = record[key];
    if (isSensitiveKey(key)) {
      record[key] = REDACTED_PLACEHOLDER;
    } else if (typeof child === 'string') {
      record[key] = redactSecretsInString(child);
    } else {
      redactInPlace(child, seen, depth + 1);
    }
  }
};

/**
 * Strip sensitive values (passwords, tokens, secrets, auth headers, …) from a
 * Sentry event before it leaves the process. Intended to be wired into every
 * SDK's `Sentry.init` via `beforeSend`. It defends the capture paths that carry
 * request-derived data:
 * - object keys in `extra`, `contexts`, `request`, breadcrumb data and
 *   stack-frame locals (anything whose key name contains a sensitive stem);
 * - sensitive query parameters embedded in the request URL / query string;
 * - secrets serialised into string fields — the event message, exception
 *   messages and breadcrumb messages — e.g. a `graphql-request` error whose
 *   message embeds the request body as JSON.
 */
export const scrubSentryEvent = <T extends ScrubbableSentryEvent>(
  event: T
): T => {
  const seen = new WeakSet<object>();

  redactInPlace(event.extra, seen, 0);
  redactInPlace(event.contexts, seen, 0);
  redactInPlace(event.request, seen, 0);

  if (typeof event.message === 'string') {
    event.message = redactSecretsInString(event.message);
  }

  // Secrets embedded in the request URL / query string are plain strings with
  // no keys for `redactInPlace` to match, so handle them explicitly.
  if (typeof event.request?.url === 'string') {
    event.request.url = redactUrl(event.request.url);
  }
  if (typeof event.request?.query_string === 'string') {
    event.request.query_string = redactQueryString(event.request.query_string);
  }

  for (const breadcrumb of event.breadcrumbs ?? []) {
    if (typeof breadcrumb.message === 'string') {
      breadcrumb.message = redactSecretsInString(breadcrumb.message);
    }
    redactInPlace(breadcrumb.data, seen, 0);
  }

  for (const exceptionValue of event.exception?.values ?? []) {
    if (typeof exceptionValue.value === 'string') {
      exceptionValue.value = redactSecretsInString(exceptionValue.value);
    }
    for (const frame of exceptionValue.stacktrace?.frames ?? []) {
      redactInPlace(frame.vars, seen, 0);
    }
  }

  return event;
};
